import { botSettingsOptions, capitalizeFirstLetter, notetakerOptions, options } from "../../utils/utils";
import CustomEnableButton from "../Common/CustomEnableButton";
import { Box, Typography } from "@mui/material";
import ColumnFilter from "../Common/customFilter";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { useState, useEffect } from "react";
import useGlobalContext from "../../context/useGlobalContext";
import ActionItemsAssigneeCell from "../ActionItems/ActionItemsAssigneeCell";

const initialSort = { sort_field: "fullName", sort_order: "asc" as "asc" | "desc" };

const getColumns = (
  handleBotScheduleChange?: (profileOrgId: string, val: string) => void,
  handleSyncChange?: (profileOrgId: string, val: string) => void,
  noteTakerFilter?: string,
  setNoteTakerFilter?: React.Dispatch<React.SetStateAction<string>>,
  calendarFilter?: string,
  setCalendarFilter?: React.Dispatch<React.SetStateAction<string>>,
  handleEmailChange?: (profileOrgId: string, val: string) => void,
  searchQuery?: string,
  handleScheduleChange?: (profileOrgId: string, value: "all" | "external" | "internal" | "ondemand") => Promise<any>,
) => {
  const { accountDetails } = useGlobalContext();
  const roleId = accountDetails?.roleId;
  const isSingle = roleId?.toLowerCase() === "user";

  const columns = [
    !isSingle && {
      accessorKey: "fullName",
      Header: (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", pr: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 15, color: Boolean(searchQuery) ? "#6036D6" : "initial" }}>
            Full Name
          </Typography>
        </Box>
      ),
      header: "Full Name",
      size: 180,
      enableSorting: true,
      Cell: ({ row }: { cell: any; row: any }) => {
        return (
          <span> {row.original?.fullName}</span>
        )
      },
    },
    {
      accessorKey: "email",
      header: "Email",
      size: 220,
      maxSize: 220,
      enableSorting: false,
      Cell: ({ row }: { cell: any; row: any }) => {
        return (
          <span> {row.original?.email}</span>
        )
      },
    },
    {
      accessorKey: "manager",
      header: "Manager",
      size: 80,
      enableSorting: false,

      Cell: ({ row }: { row: any }) => {
        const integrationUsers =
        (row.original?.activeUsers || []).map((user: any) => ({
          profileOrgRelationshipId: user?.profileOrgRelationshipId,
          profileId: user?.profileId,
          name: user?.name,
          email: user?.email,
          pictureUrl: user?.pictureUrl || "",
        }));

        const selectedManagerId =
          row.original?.managerProfileId;

        const selectedManager =
          integrationUsers.find(
            (user: any) =>
              user.profileId === selectedManagerId
          ) || {
            name: row.original?.managerName,
            email: row.original?.managerEmail,
          };

        return (
          <ActionItemsAssigneeCell
            email={selectedManagerId}
            name={selectedManager?.name}
            participant={selectedManager}
            attendeeParticipants={integrationUsers}
            canEdit={!isSingle}
            row={row}
            showCrossIcon={true}
            rowAction={{
              updateAssigneeCallback: (
                rowData: any,
                selectedManagerProfileOrgRelId: string | null
              ) => {
                const selectedUser = integrationUsers.find(
                  (user: any) =>
                    user?.profileOrgRelationshipId === selectedManagerProfileOrgRelId
                );
                row.original?.handleManagerUpdate?.(
                  rowData.original?.employeeProfileId,
                  selectedUser?.profileId || null
                );
              },
            }}
          />
        );
      },
    },
    {
      accessorKey: "department",
      header: "Department",
      size: 120,
      enableSorting: false,
      Cell: ({ row }: { row: any }) => {
        const dept = row.original.department;
        return <Box>{dept ? capitalizeFirstLetter(dept) : "-"}</Box>;
      },
    },
    {
      accessorKey: "analyzer",
      Header: isSingle ? "Notetaker" : (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", pr: 1 }}
        >
          <Box display="flex" alignItems="center">
            <Typography sx={{ fontWeight: 600, fontSize: 15, color: noteTakerFilter ? "#6036D6" : "initial" }}>
              Notetaker
            </Typography>
          </Box>
          <ColumnFilter
            currentValue={noteTakerFilter ?? ""}
            onApply={(val) => {
              setNoteTakerFilter?.(val as string);
            }}
            options={options}
            type= "single"
            showSections={{
              options: true,
              sort: false,
            }}
          />
        </Box>
      ),
      size: 120,
      enableSorting: false,
      Cell: ({ row }: { row: any }) => {
        const profileOrgId = row.original.profileOrgId;
        const initialValue = row.original.analyzer;
        const { showSnackbar } = useDisplaySnackbar();
        const [selectedValue, setSelectedValue] = useState(initialValue);

        const handleChange = async (val: string) => {
          const prevVal = selectedValue;
          setSelectedValue(val);
          try {
            await handleBotScheduleChange?.(profileOrgId, val.toLowerCase());
          } catch (error) {
            setSelectedValue(prevVal);
            showSnackbar("error", "Failed to update user settings");
          }
        };
        return (
          <CustomEnableButton
            options={notetakerOptions}
            selected={row.original.analyzer}
            onChange={handleChange}
            variant="filled"
          />
        );
      },
    },
    {
      accessorKey: "userBotSettings",
      header: "Bot Settings",
      size: 220,
      maxSize: 240,
      enableSorting: false,
      Cell: ({ row }: { row: any }) => {
        const profileOrgId = row.original.profileOrgId;
        const initialValue = row.original.userBotSettings;
        const [selectedValue, setSelectedValue] = useState(initialValue);
        const { showSnackbar } = useDisplaySnackbar();

        useEffect(() => {
          setSelectedValue(initialValue);
        }, [initialValue]);
        
        const handleChange = async (val: string) => {
          const nextValue = val as "all" | "external" | "internal" | "ondemand";
          const previousValue = selectedValue;
          setSelectedValue(nextValue);

          try {
            await handleScheduleChange?.(profileOrgId, nextValue);
          } catch (error) {
            setSelectedValue(previousValue);
            showSnackbar("error", "Failed to update bot settings");
          }
        };

        return (
          <CustomEnableButton
            options={botSettingsOptions}
            selected={selectedValue}
            onChange={handleChange}
            variant="text"
            disabled={row.original.analyzer !== "Enabled"}
          />
        );
      }
    },
    {
      accessorKey: "calendar",
      Header: isSingle ? "Calendar" : (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", pr: 1 }}
        >
          <Box display="flex" alignItems="center">
            <Typography sx={{ fontWeight: 600, fontSize: 15, color: calendarFilter ? "#6036D6" : "initial" }}>
              Calendar
            </Typography>
          </Box>
          <ColumnFilter
            currentValue={calendarFilter ?? ""}
            onApply={(val) => {
                setCalendarFilter?.(val as string);
            }}
            options={options}
            type= "single"
            showSections={{
              options: true,
              sort: false,
            }}
          />
        </Box>
      ),
      size: 100,
      enableSorting: false,
      Cell: ({ row }: { row: any }) => {
        const profileOrgId = row.original.profileOrgId;
        const initialValue = row.original.calendar;
        const { showSnackbar } = useDisplaySnackbar();
        const [selectedValue, setSelectedValue] = useState(initialValue);
        const handleChange = async (val: string) => {
          const prevVal = selectedValue;
          setSelectedValue(val);
          try {
            await handleSyncChange?.(profileOrgId, val.toLowerCase());
          } catch (error) {
            setSelectedValue(prevVal);
            showSnackbar("error", "Failed to update user settings");
          }
        };
        return (
          <CustomEnableButton
            options={notetakerOptions}
            selected={row.original.calendar}
            onChange={handleChange}
            variant="text"
          />
        );
      },
    },
    {
      accessorKey: "emailSyncEnabled",
      Header: (
          <Box display="flex" alignItems="center"ml={1}>
            <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
              Email Sync
            </Typography>
          </Box>
      ),
      size: 100,
      enableSorting: false,
      Cell: ({ row }: { row: any }) => {
        const profileOrgId = row.original.profileOrgId;
        const initialValue = row.original.calendar;
        const { showSnackbar } = useDisplaySnackbar();
        const [selectedValue, setSelectedValue] = useState(initialValue);
        const handleChange = async (val: string) => {
          const prevVal = selectedValue;
          setSelectedValue(val);
          try {
            await handleEmailChange?.(profileOrgId, val.toLowerCase());
          } catch (error) {
            setSelectedValue(prevVal);
            showSnackbar("error", "Failed to update user settings");
          }
        };
        return (
          <CustomEnableButton
            options={notetakerOptions}
            selected={row.original.emailSyncEnabled}
            onChange={handleChange}
            variant="text"
          />
        );
      },
    },
  ];
  return columns.filter(Boolean);
};

export { getColumns, initialSort };
